/**
 * Place search via Photon (OpenStreetMap-based), usable from the browser without an API key.
 */

function formatPhotonLabel(p) {
  const parts = [
    p.name,
    p.housenumber && p.street ? `${p.housenumber} ${p.street}` : p.street,
    p.city || p.town || p.village || p.district,
    p.state,
    p.country,
  ].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return p.name || 'Place';
}

/**
 * Normalized address fields from a Photon GeoJSON feature.
 */
export function parsePhotonFeature(feature) {
  if (!feature) return null;
  const p = feature.properties || {};
  const c = feature.geometry?.coordinates;
  if (!c || c.length < 2) return null;
  const [lngVal, latVal] = c;
  const cityNameRaw =
    p.city ||
    p.town ||
    p.village ||
    p.district ||
    p.municipality ||
    (typeof p.locality === 'string' ? p.locality : '') ||
    '';
  const cityName = typeof cityNameRaw === 'string' ? cityNameRaw.trim() : '';
  const streetLine = [p.housenumber, p.street].filter(Boolean).join(' ').trim();
  const fullAddress = streetLine || (p.name && p.street ? `${p.name}` : '') || p.name || '';
  const country =
    typeof p.country === 'string' && p.country.trim()
      ? p.country.trim()
      : typeof p.countrycode === 'string' && p.countrycode.toLowerCase() === 'in'
        ? 'India'
        : typeof p.countrycode === 'string'
          ? p.countrycode.toUpperCase()
          : '';
  const postcode = typeof p.postcode === 'string' ? p.postcode.trim() : '';

  return {
    lat: latVal,
    lng: lngVal,
    label: formatPhotonLabel(p),
    cityName: cityName || null,
    country: country || '',
    postcode,
    state: typeof p.state === 'string' ? p.state.trim() : '',
    street: typeof p.street === 'string' ? p.street.trim() : '',
    housenumber: typeof p.housenumber === 'string' ? p.housenumber.trim() : '',
    name: typeof p.name === 'string' ? p.name.trim() : '',
    /** One line for shop street address */
    fullAddressLine: fullAddress,
    /** Useful when fullAddressLine is a POI name */
    placeName: typeof p.name === 'string' ? p.name.trim() : '',
  };
}

/**
 * @param {string} query
 * @param {{ lat?: number, lon?: number }} [bias]
 * @returns {Promise<Array<{ id: string } & ReturnType<typeof parsePhotonFeature>>>}
 */
export async function searchPlacesPhoton(query, bias = {}) {
  const q = query.trim();
  if (!q) return [];

  const params = new URLSearchParams({ q, limit: '8' });
  const { lat, lon } = bias;
  if (lat != null && lon != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lon))) {
    params.set('lat', String(lat));
    params.set('lon', String(lon));
  }

  const url = `https://photon.komoot.io/api/?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Search failed');

  const data = await res.json();
  const out = [];
  let i = 0;
  for (const f of data.features || []) {
    const parsed = parsePhotonFeature(f);
    if (!parsed) continue;
    out.push({
      ...parsed,
      id: `r-${i}-${parsed.lat}-${parsed.lng}`,
    });
    i += 1;
  }
  return out;
}

/**
 * Reverse geocode: fill address from coordinates (e.g. after map click).
 */
export async function reverseGeocodePhoton(lat, lng) {
  const url = `https://photon.komoot.io/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const f = data.features?.[0];
  return f ? parsePhotonFeature(f) : null;
}
